import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

export interface RangeBidirectionalSliderSettings {
  apiUrl?: string;
  title?: string;
  widgetId?: string;
  label?: string;
  min?: number;
  max?: number;
  defaultValue?: number;
  step?: number;
  key?: string;
  filterKey?: string;
  isQueryParam?: boolean;
  triggerApiOnRelease?: boolean;
  onChangeEndApiUrl?: string;
  onChangeEndUrl?: string;
  method?: 'get' | 'post';
  customPayload?: Record<string, unknown>;
  filterListener?: string | string[];
}

@Component({
  selector: 'app-range-bidirectional-slider',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSliderModule,
  ],
  templateUrl: './range-bidirectional-slider.component.html',
  styleUrl: './range-bidirectional-slider.component.scss',
})
export class RangeBidirectionalSliderComponent implements OnInit, OnChanges {
  @Input() data?: RangeBidirectionalSliderSettings & Record<string, any>;
  @Input() label = 'Slider';
  @Input() min = 0;
  @Input() max = 20;
  @Input() defaultValue = 0;
  @Input() step = 1;
  @Input() onChangeEnd?: (value: number) => void;

  @Output() valueChange = new EventEmitter<number>();
  @Output() changeEnd = new EventEmitter<number>();

  value = 0;
  errorMsg = '';

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    if (this.data?.apiUrl) {
      this.configService.getConfig(this.data.apiUrl, this.data).subscribe({
        next: (response) => this.applySettings({
          ...(response?.data ?? response),
          ...this.data,
        }),
        error: () => this.applySettings(this.data),
      });
      return;
    }

    this.applySettings(this.data);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.applySettings(this.data);
      return;
    }

    if (
      changes['min'] ||
      changes['max'] ||
      changes['defaultValue'] ||
      changes['step']
    ) {
      this.normalizeSettings();
      this.value = this.clampToStep(this.value);
    }
  }

  onSliderInput(value: number | null): void {
    this.setValue(value, false);
  }

  onNumberInput(value: string | number | null): void {
    this.setValue(value, false);
  }

  onRelease(): void {
    this.setValue(this.value, true);
  }

  onInputCommit(): void {
    this.setValue(this.value, true);
  }

  protected applySettings(settings?: RangeBidirectionalSliderSettings): void {
    const source = settings ?? {};
    this.label = source.label ?? this.data?.title ?? this.label;
    this.min = this.toNumber(source.min, this.min);
    this.max = this.toNumber(source.max, this.max);
    this.step = this.toNumber(source.step, this.step || 1);
    this.defaultValue = this.toNumber(source.defaultValue, this.defaultValue);
    this.normalizeSettings();
    this.value = this.clampToStep(this.defaultValue);
    this.errorMsg = '';
    this.valueChange.emit(this.value);
  }

  protected setValue(rawValue: string | number | null | undefined, emitEnd: boolean): void {
    const numericValue = this.toNumber(rawValue, this.value);
    this.value = this.clampToStep(numericValue);
    this.errorMsg = '';
    this.valueChange.emit(this.value);

    if (emitEnd) {
      this.emitChangeEnd();
    }
  }

  protected emitChangeEnd(): void {
    this.changeEnd.emit(this.value);
    this.onChangeEnd?.(this.value);

    const filterKey = this.data?.filterKey || this.data?.key;
    if (filterKey) {
      this.globalFilterService.updateFilterSelection(
        filterKey,
        [this.value],
        this.data?.isQueryParam,
        this.data?.widgetId,
        true,
        this.data?.filterListener,
      );
    }

    const apiUrl = this.data?.onChangeEndApiUrl;
    if (apiUrl && this.data?.triggerApiOnRelease !== false) {
      const payloadData = {
        ...this.data,
        customPayload: {
          ...(this.data?.customPayload ?? {}),
          selectedValue: this.value,
        },
      };

      this.configService
        .getConfig(apiUrl, payloadData, undefined, this.data?.method)
        .subscribe({
          next: () => {},
          error: () => {
            this.errorMsg = 'Unable to update selected value.';
          },
        });
    } else if (this.data?.onChangeEndUrl) {
      this.http.post(this.data.onChangeEndUrl, { value: this.value }).subscribe({
        error: () => {
          this.errorMsg = 'Unable to update selected value.';
        },
      });
    }
  }

  protected normalizeSettings(): void {
    if (this.max < this.min) {
      [this.min, this.max] = [this.max, this.min];
    }

    if (!Number.isFinite(this.step) || this.step <= 0) {
      this.step = 1;
    }

    this.defaultValue = this.clamp(this.defaultValue);
  }

  protected clampToStep(value: number): number {
    const clamped = this.clamp(value);
    const stepsFromMin = Math.round((clamped - this.min) / this.step);
    const stepped = this.min + stepsFromMin * this.step;
    return Number(this.clamp(stepped).toFixed(10));
  }

  protected clamp(value: number): number {
    return Math.min(this.max, Math.max(this.min, value));
  }

  protected toNumber(value: unknown, fallback: number): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }
}
